/**
 * What you can do with the building you right-clicked.
 *
 * HTML above the canvas, for the same reason every other overlay in this client
 * is: the game draws into a small buffer scaled up by a whole number, so text
 * inside it comes out smeared. Menus are interface, not world.
 *
 * **It opens at the cursor, not over the building.** A tooltip names the thing
 * it points at and so belongs above it; a menu is a list being read, and a list
 * that hangs off a roof drifts away when the camera turns. The tooltip is still
 * the thing that says what the building is — this only says what can be done.
 *
 * **Entries that cannot be used are shown, not hidden.** A menu whose contents
 * change shape between two right-clicks is one nobody learns; a greyed line says
 * "this exists and not now", which is the true thing in both cases where it
 * happens today — a store with nothing in it, and a demolition that has not been
 * built yet.
 */

export interface MenuEntry {
  label: string;
  /** Said under the label, quietly, when there is a reason worth giving. */
  hint?: string;
  enabled: boolean;
  choose?: () => void;
}

export class BuildingMenu {
  private readonly root = document.createElement('div');

  constructor(private readonly container: HTMLElement) {
    this.root.className = 'building-menu';
    this.root.hidden = true;
    // A right-click inside the menu is the menu's own business. Without this
    // the browser's context menu opens on top of it.
    this.root.addEventListener('contextmenu', (event) => event.preventDefault());
    this.container.appendChild(this.root);
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  /**
   * Show a list at a point on the canvas.
   *
   * The point is in the container's coordinates, which is what the click
   * already gives once the canvas offset is taken off — the same measurement
   * `ScreenPoint` makes for everything else laid over the canvas.
   */
  show(x: number, y: number, entries: MenuEntry[], title?: string): void {
    if (entries.length === 0) {
      this.hide();
      return;
    }

    const rows: HTMLElement[] = [];
    if (title) {
      const head = document.createElement('div');
      head.className = 'building-menu-title';
      head.textContent = title;
      rows.push(head);
    }

    for (const entry of entries) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'building-menu-item';
      item.disabled = !entry.enabled;

      const label = document.createElement('span');
      label.className = 'building-menu-label';
      label.textContent = entry.label;
      item.appendChild(label);

      if (entry.hint) {
        const hint = document.createElement('span');
        hint.className = 'building-menu-hint';
        hint.textContent = entry.hint;
        item.appendChild(hint);
      }

      if (entry.enabled && entry.choose) {
        const chosen = entry.choose;
        item.addEventListener('click', () => {
          // Closed first: the action can take a trip to the server, and a menu
          // left standing open over it reads as one that did not hear the
          // click.
          this.hide();
          chosen();
        });
      }

      rows.push(item);
    }

    this.root.replaceChildren(...rows);
    this.root.hidden = false;
    this.place(x, y);
  }

  /**
   * Postaw pudełko tam, gdzie kliknięto.
   *
   * Mierzone po wstawieniu do układu, bo rozmiar zależy od zawartości: menu
   * otwarte przy prawej krawędzi inaczej z niej zwisa.
   */
  private place(x: number, y: number): void {
    const box = this.root.getBoundingClientRect();
    const room = this.container.getBoundingClientRect();
    this.root.style.left = `${Math.round(Math.min(x, room.width - box.width - 4))}px`;
    this.root.style.top = `${Math.round(Math.min(y, room.height - box.height - 4))}px`;
  }

  /**
   * Pokaż w tym samym miejscu coś, co nie jest listą.
   *
   * Przekazywanie surowców to formularz, a nie wybór pozycji — ale otwiera się
   * tam, gdzie kliknięto, i zamyka tak samo, więc jest to to samo pudełko z inną
   * zawartością, a nie druga nakładka o własnych regułach.
   */
  showPanel(x: number, y: number, body: HTMLElement, title: string): void {
    const head = document.createElement('div');
    head.className = 'building-menu-title';
    head.textContent = title;

    this.root.replaceChildren(head, body);
    this.root.hidden = false;
    this.place(x, y);
  }

  hide(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.root.replaceChildren();
  }

  dispose(): void {
    this.root.remove();
  }
}
